import {
  AppstoreAddOutlined,
  BankOutlined,
  DollarCircleOutlined,
  PieChartOutlined,
  ShoppingCartOutlined,
  TagsOutlined,
} from '@ant-design/icons'
import { Card, List, Spin, Tag } from 'antd'
import { useMemo } from 'react'

import { PTO_V3_SELLING_STATUS_CONFIG, PtoV3SellingStatusEnum } from 'constants/pto-v3'
import { usePTOV3Context } from 'context/pto-v3-context-provider'
import { usePoolInfoV3 } from 'hooks/pto-v3/use-pool-info-v3'
import { useVaultBalanceV3 } from 'hooks/pto-v3/use-vault-balance-v3'
import { formatNumber, safeDivide } from 'utils/formatted-number'

/**
 * PTO v3 statistics. Price per token / Funding target are shown only while On Sale.
 * Vault balance is read from the ERC-3643 token (whole PTOs, 0 decimals); the vault is
 * funded by the contracts when the pool is created.
 */
const Overview = () => {
  const { loading: detailLoading, ptoDetail, contractAddress } = usePTOV3Context()
  const { vaultBalance } = useVaultBalanceV3(contractAddress, ptoDetail?.vaultAddress)
  const { poolInfo, loading: poolLoading } = usePoolInfoV3(contractAddress)
  const loading = detailLoading || poolLoading

  const detail = useMemo(() => {
    const sellingStatus: PtoV3SellingStatusEnum =
      ptoDetail?.sellingStatus ?? PtoV3SellingStatusEnum.OFF_SALE
    const isOnSale = sellingStatus === PtoV3SellingStatusEnum.ON_SALE
    const sellingStatusConfig =
      PTO_V3_SELLING_STATUS_CONFIG[sellingStatus] ||
      PTO_V3_SELLING_STATUS_CONFIG[PtoV3SellingStatusEnum.OFF_SALE]

    // token cap - the API keeps the v1/v2 name maxNFTSupply
    const totalSupply = Number(ptoDetail?.maxNFTSupply || 0)
    const pricePerPTO = Number(ptoDetail?.pricePerPTO || 0)
    const fundingTarget = totalSupply * pricePerPTO
    const totalPTOsPurchase = poolInfo?.totalPTOsPurchase || 0
    const percent = safeDivide(totalPTOsPurchase, poolInfo?.maxPTOAmount || totalSupply) * 100

    return [
      {
        title: 'Funding target (USDC)',
        value: isOnSale ? formatNumber(fundingTarget, 3) : '--',
        icon: (
          <DollarCircleOutlined
            className="icons-style"
            style={{ background: 'lightgreen', color: 'green' }}
          />
        ),
      },
      {
        title: 'Total supply',
        value: totalSupply,
        icon: (
          <AppstoreAddOutlined
            className="icons-style"
            style={{ background: 'lightblue', color: 'blue' }}
          />
        ),
      },
      {
        title: 'Price per token (USDC)',
        value: isOnSale ? formatNumber(pricePerPTO, 2) : '--',
        icon: (
          <DollarCircleOutlined
            className="icons-style"
            style={{ background: 'lightgreen', color: 'darkgreen' }}
          />
        ),
      },
      {
        title: 'Vault balance (PTO)',
        value: vaultBalance === null ? '--' : vaultBalance,
        icon: (
          <BankOutlined
            className="icons-style"
            style={{ background: 'lightyellow', color: 'goldenrod' }}
          />
        ),
      },
      {
        title: 'Total PTOs Purchased',
        value: totalPTOsPurchase,
        icon: (
          <ShoppingCartOutlined
            className="icons-style"
            style={{ background: 'lightcoral', color: 'red' }}
          />
        ),
      },
      {
        title: 'Sold',
        value: `${percent.toFixed(2)}%`,
        icon: (
          <PieChartOutlined
            className="icons-style"
            style={
              totalPTOsPurchase > 0 && poolInfo && totalPTOsPurchase >= poolInfo.maxPTOAmount
                ? { background: 'lightgreen', color: 'darkgreen' }
                : { background: 'lightyellow', color: 'orange' }
            }
          />
        ),
      },
      {
        title: 'Selling status',
        value: <Tag color={sellingStatusConfig.color}>{sellingStatusConfig.label}</Tag>,
        icon: (
          <TagsOutlined
            className="icons-style"
            style={{ background: 'lightblue', color: 'steelblue' }}
          />
        ),
      },
    ]
  }, [ptoDetail, vaultBalance, poolInfo])

  return (
    <List
      grid={{ gutter: 16, column: 4 }}
      dataSource={detail}
      className="detail-card"
      renderItem={(item, index) => (
        <List.Item key={index}>
          <Card className={'card-style'}>
            <div className={'content-style'}>
              <div>{item.icon}</div>
              {!loading ? (
                <div>
                  <div className={'title-style'}>{item.title}</div>
                  <div className={'value-style'}>{item.value}</div>
                </div>
              ) : (
                <Spin />
              )}
            </div>
          </Card>
        </List.Item>
      )}
    />
  )
}

export default Overview
